import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';

import {URLS} from 'constants/routes';

import {iconPaths, particles, trustAvatars} from '../../data';
import OrbitStage from './OrbitStage';

/**
 * Hero 首屏：渐变底 + 网格 + 粒子 + 左文案（徽章/大标题/双 CTA/信任徽章）+ 右轨道舞台 + 滚动指示。
 * 极光背景已按业务要求移除（性能）；.aurora-* 样式仍被 CTA 区使用，勿删 CSS。
 * 「申请合作」接 CooperationModal（与导航「合作申请」共用组件实例逻辑，2026-09 由「预约演示」更名）。
 */
const Hero: FC<{onDemoClick: () => void}> = ({onDemoClick}) => {
  const {t} = useTranslation('home');

  return (
    <section className="relative lg:pt-20 gradient-hero grid-bg noise-overlay overflow-hidden">
      {/* Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
        {particles.map((particle) => (
          <div
            className="particle"
            key={`${particle.top}-${particle.left}`}
            style={{animationDelay: particle.animationDelay, left: particle.left, top: particle.top}}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-[90rem] mx-auto px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* 左列文案 */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md chip-solid">
              <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
              <span className="text-sm text-slate-600">{t('hero.badge')}</span>
            </div>

            <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-slate-900 leading-[1.1] tracking-tight">
              {t('hero.title1')}
              <br />
              <span className="text-gradient glow-text">{t('hero.title2')}</span>
            </h1>

            <p className="lg:text-lg text-slate-500 leading-relaxed max-w-xl">{t('hero.subtitle')}</p>

            <div className="flex flex-wrap gap-4">
              <a
                className="btn-primary inline-flex items-center gap-2 text-white font-semibold px-8 py-4 rounded-md text-base"
                href={URLS.login}
              >
                {t('hero.freeTrial')}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path d={iconPaths.arrowRight} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <button
                className="btn-secondary inline-flex items-center gap-2 text-indigo-600 font-semibold px-8 py-4 rounded-md text-base"
                onClick={onDemoClick}
                type="button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  {iconPaths.play.map((d) => (
                    <path key={d} d={d} strokeLinecap="round" strokeLinejoin="round" />
                  ))}
                </svg>
                {t('hero.demo')}
              </button>
            </div>

            {/* 信任徽章 */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-2">
                {trustAvatars.map((src) => (
                  <img
                    alt=""
                    className="w-8 h-8 rounded-full border-2 border-white object-cover object-top"
                    decoding="async"
                    key={src}
                    src={src}
                  />
                ))}
                <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-xs text-slate-600 font-medium">
                  +
                </div>
              </div>
              <div className="text-sm text-slate-500">
                <span className="text-slate-900 font-semibold">{t('hero.trustedCount')}</span>
                {t('hero.trustedSuffix')}
              </div>
            </div>
          </div>

          {/* 右列轨道舞台 */}
          <OrbitStage />
        </div>
      </div>

      {/* 滚动指示 */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50 z-10">
        <span className="text-xs text-slate-400">{t('hero.scrollHint')}</span>
        <div className="w-5 h-8 rounded-full border border-slate-300 flex justify-center pt-1">
          <div className="w-1 h-2 bg-indigo-500 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
